import type { SepLabel } from '../sim/playSim';
import type { Tunables } from '../sim/tunables';
import { el } from './dom';
import { layoutIcons, type IconAnchor } from './iconLayout';

const SVG_NS = 'http://www.w3.org/2000/svg';
const TAGS: Record<SepLabel, string> = { open: 'OPEN', contested: 'CONTESTED', covered: 'COVERED' };
const RING_R = 44;
const RING_C = 2 * Math.PI * RING_R;

export interface IconHandlers {
  /** Finger down on an icon: start the power meter. */
  onHoldStart(index: number): void;
  /** Finger up: throw. held is the seconds the finger was down. */
  onRelease(index: number, held: number): void;
}

interface Icon {
  node: HTMLButtonElement;
  tag: HTMLElement;
  line: SVGLineElement;
  fill: SVGCircleElement;
  band: SVGCircleElement;
  state: SepLabel | null;
}

/**
 * Round A to E icons over the receivers (GDD 6.4, reference 02), coloured by separation, de-overlapped.
 * Tap for a quick throw; hold to fill the power ring around the icon (with the ideal band drawn on it) and release to throw.
 */
export class ReceiverIcons {
  readonly root = el('div', 'icons');
  private readonly lines = document.createElementNS(SVG_NS, 'svg');
  private readonly hint = el('div', 'hint');
  private icons: Icon[] = [];
  private readonly anchors: IconAnchor[] = [];
  private holding = -1;
  private holdStart = 0;

  constructor(
    private readonly t: Tunables,
    private readonly h: IconHandlers,
  ) {
    this.lines.classList.add('icons-lines');
    this.hint.innerHTML = '<span class="hint-key">TAP</span> QUICK THROW <span class="hint-sep"></span> <span class="hint-key">HOLD</span> SET POWER';
    this.root.append(this.lines, this.hint);
    this.root.hidden = true;
  }

  setReceivers(labels: string[]): void {
    for (const i of this.icons) {
      i.node.remove();
      i.line.remove();
    }
    this.anchors.length = 0;
    this.holding = -1;
    this.icons = labels.map((label, index) => {
      const node = el('button', 'ricon');
      node.dataset.testid = `icon-${label}`;
      node.setAttribute('aria-label', `Throw to ${label}`);
      const ring = document.createElementNS(SVG_NS, 'svg');
      ring.setAttribute('viewBox', '0 0 100 100');
      ring.classList.add('ricon-ring');
      const band = document.createElementNS(SVG_NS, 'circle');
      const fill = document.createElementNS(SVG_NS, 'circle');
      for (const c of [band, fill]) {
        c.setAttribute('cx', '50');
        c.setAttribute('cy', '50');
        c.setAttribute('r', String(RING_R));
        c.setAttribute('fill', 'none');
        c.setAttribute('transform', 'rotate(-90 50 50)');
      }
      band.classList.add('ricon-band');
      fill.classList.add('ricon-fill');
      fill.setAttribute('stroke-dasharray', `0 ${RING_C}`);
      ring.append(band, fill);
      const letter = el('span', 'ricon-letter', label);
      const tag = el('span', 'ricon-tag');
      node.append(ring, letter, tag);
      node.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        node.setPointerCapture?.(e.pointerId);
        this.holding = index;
        this.holdStart = performance.now();
        node.classList.add('holding');
        this.h.onHoldStart(index);
      });
      const up = (e: PointerEvent) => {
        if (this.holding !== index) return;
        e.preventDefault();
        const held = (performance.now() - this.holdStart) / 1000;
        this.holding = -1;
        node.classList.remove('holding');
        this.h.onRelease(index, held);
      };
      node.addEventListener('pointerup', up);
      node.addEventListener('pointercancel', up);
      node.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.h.onHoldStart(index);
          this.h.onRelease(index, 0);
        }
      });
      const line = document.createElementNS(SVG_NS, 'line');
      this.lines.appendChild(line);
      this.root.appendChild(node);
      this.anchors.push({ id: index, x: 0, y: 0 });
      return { node, tag, line, fill, band, state: null };
    });
  }

  show(on: boolean): void {
    this.root.hidden = !on;
    if (!on) this.holding = -1;
  }

  get holdingIndex(): number {
    return this.holding;
  }

  /** Seconds the current hold has lasted. */
  holdSeconds(): number {
    return this.holding < 0 ? 0 : (performance.now() - this.holdStart) / 1000;
  }

  /** Power ring for the held icon: power 0 to 100, ideal band lo to hi. */
  setPower(index: number, power: number, lo: number, hi: number): void {
    const icon = this.icons[index];
    if (!icon) return;
    icon.fill.setAttribute('stroke-dasharray', `${(RING_C * power) / 100} ${RING_C}`);
    icon.band.setAttribute('stroke-dasharray', `0 ${(RING_C * lo) / 100} ${(RING_C * (hi - lo)) / 100} ${RING_C}`);
    icon.node.classList.toggle('in-band', power >= lo && power <= hi);
  }

  /** Separation labels, refreshed on the GDD's 100 ms tick. */
  setStates(states: SepLabel[]): void {
    states.forEach((s, i) => {
      const icon = this.icons[i];
      if (!icon || icon.state === s) return;
      icon.state = s;
      icon.node.dataset.state = s;
      icon.tag.textContent = TAGS[s];
    });
  }

  /** Screen anchors above each receiver's head, every frame. */
  place(points: { x: number; y: number }[], width: number, height: number): void {
    const u = this.t.ui;
    points.forEach((p, i) => {
      const a = this.anchors[i];
      if (a) {
        a.x = p.x;
        a.y = p.y;
      }
    });
    const placed = layoutIcons(this.anchors, {
      size: u.iconSizePx,
      gap: u.iconGapPx,
      width,
      height,
      edgeFraction: u.safeEdgeFraction,
      bottomFraction: u.safeBottomFraction,
      thumbWidthFraction: u.thumbZoneWidthFraction,
      thumbHeightFraction: u.thumbZoneHeightFraction,
    });
    for (const p of placed) {
      const icon = this.icons[p.id];
      if (!icon) continue;
      icon.node.style.transform = `translate(${p.x}px, ${p.y}px)`;
      icon.line.setAttribute('x1', String(p.x));
      icon.line.setAttribute('y1', String(p.y));
      icon.line.setAttribute('x2', String(p.anchorX));
      icon.line.setAttribute('y2', String(p.anchorY));
      icon.line.style.display = p.moved ? '' : 'none';
    }
  }
}
